import { QueryHookOptions } from '@apollo/client';
import { Paths } from 'type-fest';

import {
  AllowListPhoneNumberFilterInput,
  AllowListPhoneNumberFragment,
  CreateAllowListPhoneNumberMutationOptions,
  DeleteAllowListPhoneNumberMutationOptions,
  GetAllowListPhoneNumbersQuery,
  GetAllowListPhoneNumbersQueryVariables,
  UpdateAllowListPhoneNumberMutationOptions,
  useDeleteAllowListPhoneNumberMutation as useApolloDeleteAllowListPhoneNumberMutation,
  useCreateAllowListPhoneNumberMutation as useApolloCreateAllowListPhoneNumberMutation,
  useUpdateAllowListPhoneNumberMutation as useApolloUpdateAllowListPhoneNumberMutation,
  useGetAllowListPhoneNumbersQuery,
} from '@occc/graphql';
import { PaginationInfo, usePaginatedQuery } from '@occc-app/hooks/usePaginatedQuery';
import { useMutation } from '@occc-app/dataAccessLayer/utils';

import { transformDataToAllowListPhoneNumbersArray } from '../allow-list.utils';

type GetAllowListPhoneNumbersProps = {
  filter: AllowListPhoneNumberFilterInput;
  pageSize?: number;
  baseOptions?: QueryHookOptions<
    AllowListPhoneNumberFragment[],
    GetAllowListPhoneNumbersQueryVariables
  >;
  paginationInfo?: PaginationInfo;
  onPageChange?: (info: PaginationInfo) => void;
};

export const useGetAllowListPhoneNumbers = ({
  filter,
  pageSize,
  baseOptions,
  paginationInfo,
  onPageChange,
}: GetAllowListPhoneNumbersProps) => {
  const response = usePaginatedQuery(
    useGetAllowListPhoneNumbersQuery,
    'getAllowListPhoneNumbers.pageInfo' satisfies Paths<GetAllowListPhoneNumbersQuery>,
    pageSize,
    {
      fetchPolicy: 'cache-and-network',
      notifyOnNetworkStatusChange: true,
      variables: {
        filter,
      },
      ...baseOptions,
      onCompleted: (data) => {
        if (baseOptions && baseOptions.onCompleted) {
          baseOptions.onCompleted(transformDataToAllowListPhoneNumbersArray(data));
        }
      },
    } as QueryHookOptions,
    paginationInfo,
    onPageChange,
  );

  return {
    ...response,
    data: response.data ? transformDataToAllowListPhoneNumbersArray(response.data) : response.data,
  };
};

export const useDeleteAllowListPhoneNumberMutation = (
  baseOptions: DeleteAllowListPhoneNumberMutationOptions,
) => useMutation(useApolloDeleteAllowListPhoneNumberMutation, baseOptions);

export const useCreateAllowListPhoneNumberMutation = (
  baseOptions: CreateAllowListPhoneNumberMutationOptions,
) => useMutation(useApolloCreateAllowListPhoneNumberMutation, baseOptions);

export const useUpdateAllowListPhoneNumberMutation = (
  baseOptions: UpdateAllowListPhoneNumberMutationOptions,
) => useMutation(useApolloUpdateAllowListPhoneNumberMutation, baseOptions);
